import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import { Bell, X, AtSign, Focus } from "lucide-react";
import { caminhoDaNotificacao } from "../utils/notificacaoLink";
import { marcarComoLida, Notificacao } from "../services/notificacoes";

interface ToastItem {
  id: string;
  notif: Notificacao;
  expiraEm: number;
}

const DURACAO_MS = 6000;
const MAX_TOASTS = 3;

/**
 * Toasts flutuantes pra notificações do app (projetos, prazos, foco, etc.).
 * Aparecem no canto inferior-direito; se o chat estiver aberto (detectado via
 * `body[data-chat-aberto]`), desloca pra esquerda do painel pra não sobrepor.
 * Some sozinho em ~6s ou ao clicar no X. Clicar no corpo abre o destino.
 *
 * Mensagens de chat NÃO entram aqui — o chat tem sua própria UX (badge,
 * Notification do navegador via `getInPageNotifAtivo`).
 */
export function ToastNotificacoes() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [chatAberto, setChatAberto] = useState<boolean>(() =>
    typeof document !== "undefined" && !!document.body.getAttribute("data-chat-aberto"),
  );
  const seenRef = useRef<Set<string>>(new Set());

  // Escuta evento SSE de nova notificação. Payload vem com a Notificacao já
  // formada (backend emite o objeto inteiro).
  useEffect(() => {
    function onNova(e: any) {
      const n = e.detail as Notificacao | undefined;
      if (!n || !n.id) return;
      // Dedup — SSE pode disparar 2x em reconexões
      if (seenRef.current.has(n.id)) return;
      seenRef.current.add(n.id);
      setToasts((arr) => {
        const item: ToastItem = { id: n.id, notif: n, expiraEm: Date.now() + DURACAO_MS };
        const novo = [item, ...arr];
        return novo.slice(0, MAX_TOASTS);
      });
    }
    window.addEventListener("growsoft:notificacao.nova", onNova);
    return () => window.removeEventListener("growsoft:notificacao.nova", onNova);
  }, []);

  // Auto-dismiss
  useEffect(() => {
    if (toasts.length === 0) return;
    const t = setInterval(() => {
      const agora = Date.now();
      setToasts((arr) => arr.filter((x) => x.expiraEm > agora));
    }, 500);
    return () => clearInterval(t);
  }, [toasts.length]);

  // Observa abrir/fechar do chat (sinalizado via body data-attr pelo ChatFloating)
  useEffect(() => {
    function checa() {
      const aberto = !!document.body.getAttribute("data-chat-aberto");
      setChatAberto(aberto);
    }
    checa();
    const obs = new MutationObserver(checa);
    obs.observe(document.body, { attributes: true, attributeFilter: ["data-chat-aberto"] });
    return () => obs.disconnect();
  }, []);

  function fechar(id: string) {
    setToasts((arr) => arr.filter((t) => t.id !== id));
  }
  function clicar(t: ToastItem) {
    if (!t.notif.lida) {
      marcarComoLida(t.notif.id).catch(() => null);
      qc.invalidateQueries({ queryKey: ["notificacoes"] });
      qc.invalidateQueries({ queryKey: ["notificacoes-count"] });
    }
    const destino = caminhoDaNotificacao(t.notif, user?.perfil);
    if (destino) navigate(destino);
    fechar(t.id);
  }

  if (toasts.length === 0) return null;

  // Em desktop: chat aberto = right-[400px], se não = right-5.
  // Em mobile (chat aberto = tela cheia): esconde os toasts pra não tampar.
  const ehMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches;
  if (ehMobile && chatAberto) return null;

  const rightClass = chatAberto ? "right-[380px]" : "right-5";

  return (
    <div className={`fixed bottom-5 ${rightClass} z-[55] flex flex-col-reverse gap-2 max-w-[340px] pointer-events-none`}>
      {toasts.map((t) => {
        const n = t.notif;
        const ehMencao = n.tipo === "tarefa_mencao";
        const ehFoco = n.tipo?.startsWith("foco_");
        const Icon = ehMencao ? AtSign : ehFoco ? Focus : Bell;
        const acento = ehMencao
          ? "border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100"
          : ehFoco
            ? "border-emerald-400 bg-emerald-50 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100"
            : "border-blue-400 bg-white text-slate-800 dark:bg-slate-800 dark:text-slate-100";
        return (
          <div
            key={t.id}
            className={`pointer-events-auto shadow-2xl rounded-lg border-l-4 ${acento} px-3 py-2 cursor-pointer hover:shadow-xl transition-all animate-[slideIn_.25s_ease-out]`}
            onClick={() => clicar(t)}
          >
            <div className="flex items-start gap-2">
              <Icon size={16} className="shrink-0 mt-0.5 opacity-80" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold leading-tight">{n.titulo}</p>
                <p className="text-xs opacity-80 line-clamp-2 mt-0.5">{n.mensagem}</p>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); fechar(t.id); }}
                className="shrink-0 opacity-60 hover:opacity-100"
                title="Dispensar"
                aria-label="Dispensar"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
